import { config } from "../../config";

import * as analytics from "../../analytics";
import {
  getAddressByUdprn,
  IAddress,
  IAddressAutocomplete,
} from "../../services/api/postcodes";
import { ActionDispatch } from "../index";

import * as types from "./action-types";
import { IUnresolvedAddress, locationType } from "./reducer";

export const actionCreators = {
  clearAddress: () => ({
    type: types.CLEAR_ADDRESS,
  }),
  clearBillingAddress: () => ({
    type: types.CLEAR_BILLING_ADDRESS,
  }),
  clearAddressSuggestions: () => ({
    type: types.CLEAR_ADDRESS_SUGGESTIONS,
  }),
  clearPostcodeSuggestions: () => ({
    type: types.CLEAR_POSTCODE_SUGGESTIONS,
  }),
  fetchAddressByUdprnAttempt: () => ({
    type: types.FETCH_ADDRESS_UDPRN_ATTEMPT,
  }),
  fetchAddressByUdprnFailure: (error: Error) => ({
    payload: { error },
    type: types.FETCH_ADDRESS_UDPRN_FAILURE,
  }),
  fetchAddressByUdprnSuccess: (data: IAddress) => ({
    payload: { data },
    type: types.FETCH_ADDRESS_UDPRN_SUCCESS,
  }),
  getAddressSuggestionsAttempt: () => ({
    type: types.GET_ADDRESS_SUGGESTIONS_ATTEMPT,
  }),
  getAddressSuggestionsFailure: (error: Error) => ({
    payload: { error },
    type: types.GET_ADDRESS_SUGGESTIONS_FAILURE,
  }),
  getAddressSuggestionsSuccess: (data: IAddressAutocomplete[]) => ({
    payload: { data },
    type: types.GET_ADDRESS_SUGGESTIONS_SUCCESS,
  }),
  resetSuggestedState: () => ({
    type: types.RESET_SUGGESTED_STATE,
  }),
  setAddressManually: (data: IUnresolvedAddress, type?: locationType) => ({
    payload: { data, type },
    type: types.SET_ADDRESS_MANUALLY,
  }),
  setBillingAddressManually: (
    data: IUnresolvedAddress,
    type?: locationType,
  ) => ({
    payload: { data, type },
    type: types.SET_BILLING_ADDRESS_MANUALLY,
  }),
  setCorrectedAddress: (data: string) => ({
    payload: { data },
    type: types.SET_CORRECTED_ADDRESS,
  }),
  setPostcodeSuggestions: (data: string[]) => ({
    payload: { data },
    type: types.SET_POSTCODE_SUGGESTIONS,
  }),
  setSelectedPostcode: (data: string) => ({
    payload: { data },
    type: types.SET_SELECTED_POSTCODE,
  }),
  setSuggestedAddress: (data: string) => ({
    payload: { data },
    type: types.SET_SUGGESTED_ADDRESS,
  }),
  setSuggestedEntry: (data: string) => ({
    payload: { data },
    type: types.SET_SUGGESTED_ENTRY,
  }),
};

/**
 * Dispatch suggestions list action for reducer
 * Call API to get suggestions list
 */
export function getAddressSuggestionsData(
  addresses: IAddressAutocomplete[],
  address: string,
): ActionDispatch<void> {
  return async (dispatch) => {
    dispatch(actionCreators.getAddressSuggestionsAttempt());

    try {
      // const data = await getSuggestedAddresses(address);
      analytics.track({
        name: analytics.AnalyticsEvents.ADDRESS_SEARCH,
        payload: {
          numberOfResults: addresses.length,
          query: address,
        },
      });
      dispatch(actionCreators.getAddressSuggestionsSuccess(addresses));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(actionCreators.getAddressSuggestionsFailure(error));
      }
    }
  };
}

/**
 * Dispatch address search action for reducer
 * Call API to get address data by udprn
 */
export function fetchAddressUdprnData(udprn: number): ActionDispatch<void> {
  return async (dispatch) => {
    dispatch(actionCreators.fetchAddressByUdprnAttempt());
    analytics.track({
      name: analytics.AnalyticsEvents.ADDRESS_SELECT,
      payload: { udprn },
    });
    try {
      const data = await getAddressByUdprn(udprn);
      dispatch(actionCreators.fetchAddressByUdprnSuccess(data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(actionCreators.fetchAddressByUdprnFailure(error));
      }
    }
  };
}

/**
 * Search for postcode areas we serve matching the short address query string
 * @param query Cleaned query string entered by the user, to be matched against postcodes (required to be uppercase, space stripped)
 */
export function findPostcodeSuggestions(query: string): ActionDispatch<void> {
  return async (dispatch) => {
    const postcodes = config.postcodeAreas;
    if (typeof postcodes === "undefined") {
      // Postcodes functionality is disabled, or misconfigured
      return;
    }
    const matches = postcodes.filter((s) => s.match(`^${query}`));
    analytics.track({
      name: analytics.AnalyticsEvents.ADDRESS_SEARCH_POSTCODE_AREA,
      payload: {
        numberOfResults: matches.length,
        query,
      },
    });
    dispatch(actionCreators.setPostcodeSuggestions(matches));
  };
}

/**
 * Select the postcode area for browsing available treatments and therapists
 * @param area postcode area - string of 2-4 characters, letters followed by numbers. Uppercase is required.
 */
export function selectPostcodeArea(area: string): ActionDispatch<void> {
  return async (dispatch) => {
    // analytics.track(analytics.AnalyticsEvents.ADDRESS_SELECT_POSTCODE_AREA, {
    //   area,
    // });
    analytics.track({
      name: analytics.AnalyticsEvents.ADDRESS_SELECT_POSTCODE_AREA,
      payload: {
        area,
      },
    });
    dispatch(actionCreators.setSelectedPostcode(area));
  };
}
